import { useEffect, useState, useCallback, useRef, memo, forwardRef, useImperativeHandle } from "react";
import { useSelector } from "react-redux";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import {
  format,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
    startOfMonth,
    endOfMonth,
    startOfQuarter,
    endOfQuarter,
    startOfYear,
    endOfYear,
    addMonths,
    subMonths,
    addQuarters,
    subQuarters,
    addYears,
    subYears,
    parseISO,
    min as dfMin,
    max as dfMax,
} from "date-fns";
import { enUS } from "date-fns/locale";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import "./sheet/hours-sheet.css";

// Helpers for computing period ranges
const startOfDaySafe = (d) => new Date(format(d, "yyyy-MM-dd"));
const endOfDaySafe = (d) => new Date(format(d, "yyyy-MM-dd"));
const getPeriodRangeForDate = (dateObj, type) => {
  switch (type) {
    case "day":
      return { from: startOfDaySafe(dateObj), to: endOfDaySafe(dateObj) };
    case "week":
      return {
        from: startOfWeek(dateObj, { weekStartsOn: 1 }),
        to: endOfWeek(dateObj, { weekStartsOn: 1 }),
      };
    case "month":
      return { from: startOfMonth(dateObj), to: endOfMonth(dateObj) };
    case "quarter":
      return { from: startOfQuarter(dateObj), to: endOfQuarter(dateObj) };
    case "year":
      return { from: startOfYear(dateObj), to: endOfYear(dateObj) };
    default:
      return { from: startOfMonth(dateObj), to: endOfMonth(dateObj) };
  }
};

const sumHoursList = (list) => {
  if (!Array.isArray(list)) return 0;
  return list.reduce((s, h) => s + (parseFloat(h?.time) || 0), 0);
};

/** Expected hours per calendar day in range from scaled weekday map (English keys: monday, …). */
const normalHoursBetween = (dateFromStr, dateToStr, scaledWeekdays) => {
  if (!scaledWeekdays || typeof scaledWeekdays !== "object" || !dateFromStr || !dateToStr) {
    return null;
  }
  const start = parseISO(dateFromStr.substring(0, 10));
  const end = parseISO(dateToStr.substring(0, 10));
  if (start > end) return null;
  let sum = 0;
  eachDayOfInterval({ start, end }).forEach((day) => {
    const key = format(day, "EEEE", { locale: enUS }).toLowerCase();
    const v = parseFloat(scaledWeekdays[key] ?? 0);
    if (!isNaN(v)) sum += v;
  });
  return sum;
};

const hoursInDateRange = (list, fromStr, toStr) => {
  if (!Array.isArray(list) || !fromStr || !toStr) return 0;
  const from = fromStr.substring(0, 10);
  const to = toStr.substring(0, 10);
  return sumHoursList(
    list.filter((h) => {
      const d = (h?.date || "").substring(0, 10);
      return d && d >= from && d <= to;
    })
  );
};

async function fetchAllHoursPaginated(baseParams) {
  const per_page = 200;
  let page = 1;
  let all = [];
  let totalCount = null;
  for (;;) {
    const response = await fetchHours({ ...baseParams, per_page, page });
    const batch = response?.data || [];
    if (totalCount === null) totalCount = response?.totalCount ?? batch.length;
    all = all.concat(batch);
    if (batch.length < per_page || all.length >= totalCount) break;
    page += 1;
  }
  return all;
}

import { nb } from "date-fns/locale";
import { fetchHours } from "../../../api/hour";
import { fetchUserWorkSchedule } from "../../../api/user";
import {
  BuildingLibraryIcon,
  DocumentTextIcon,
  WrenchIcon,
  FunnelIcon,
  CalendarIcon,
  AdjustmentsHorizontalIcon,
  ArrowTrendingUpIcon,
} from "@heroicons/react/24/outline";

import { ProjectSelector } from "../../utils/moduleSelectors/projectSelector";
import { ModernProjectActivitySelectorField } from "../../utils/moduleSelectors/ModernProjectActivitySelectorField";
import { PeriodSelector } from "./period_selector";
import { ApproveHourDialog } from "./approveHourDialog";
import { ProjectTitle, ReportTitle, WorkOrderTitle } from "../../Utils";
import { hourHasProjectActivity, projectActivityLine } from "./projectActivityDisplay";
import { PMDatePicker } from "../../utils/PMDatePicker";
import { PMDialog } from "../../utils/PMDialog";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { HoursUkeStripBody } from "./sheet/HoursUkeStripBody";
import { HourBillingSelection } from "./HourBillingDialog";
import { AgreedOvertimeBadge } from "./AgreedOvertimeBadge";

const TripletexSyncBadge = ({ hour }) => {
  const { t } = useTranslation();
  const isSynced = hour.tripletex_entry_id && hour.last_synced_at;
  const blockHint =
    hour.tripletex_sync_block_reason_message ||
    hour.tripletex_sync_block_reason ||
    "";

  if (!isSynced) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600"
        title={
          blockHint
            ? `${t("Ikke synkronisert med Tripletex")}: ${blockHint}`
            : t("Ikke synkronisert med Tripletex")
        }
      >
        <XCircleIcon className="h-3 w-3" />
        <span className="hidden sm:inline">{t("Ikke synkronisert")}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" title={t("Synkronisert med Tripletex")}>
      <CheckCircleIcon className="h-3 w-3" />
      <span className="hidden sm:inline">{t("Synkronisert")}</span>
    </span>
  );
};

const HoursViewComponentInternal = forwardRef(function HoursViewComponentInternal({
  user_id,
  onHourClick,
  onNewHourClick,
  showFilters = true,
  layout = "default",
  initialView = "week",
  initialDate = null,
  initialDateFrom = null,
  initialDateTo = null,
  initialPeriod = "month",
  onStateChange = null,
  onWeekHoursLabel = null,
  className = "",
  pushHour = null,
  removeHourId = null,
}, ref) {
  const isUkeStrip = layout === "uke-strip";
  const [infoBannerHidden, setInfoBannerHidden] = useState(false);
  const { t } = useTranslation();
  const today = new Date();

  // Store the current date to prevent it from being reset on re-mount
  const currentDateRef = useRef(today.toISOString().substring(0, 10));
  // Ref for the hidden date picker triggered by clicking the date label (calendar.js pattern)
  const datePickerInputRef = useRef(null);
  
  // Track previous filter values to prevent unnecessary updates
  const prevFiltersRef = useRef(null);

  // Filters state
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedProjectActivity, setSelectedProjectActivity] = useState(null);
  const [showFiltersMenu, setShowFiltersMenu] = useState(false);
  const [periodType, setPeriodType] = useState(initialPeriod);

  const [controller, setController] = useState(() => {
    const baseDate = (initialDate || currentDateRef.current || today.toISOString().substring(0, 10));
    let dateFrom, dateTo;
    if (initialView === "day") {
      dateFrom = format(new Date(baseDate), "yyyy-MM-dd");
      dateTo = format(new Date(baseDate), "yyyy-MM-dd");
    } else if (initialView === "week") {
      dateFrom = format(startOfWeek(new Date(baseDate), { weekStartsOn: 1 }), "yyyy-MM-dd");
      dateTo = format(endOfWeek(new Date(baseDate), { weekStartsOn: 1 }), "yyyy-MM-dd");
    } else if (initialView === "period") {
      if (initialDateFrom && initialDateTo) {
        dateFrom = format(new Date(initialDateFrom), "yyyy-MM-dd");
        dateTo = format(new Date(initialDateTo), "yyyy-MM-dd");
      } else {
        // Default period range based on periodType
        const { from, to } = getPeriodRangeForDate(new Date(baseDate), initialPeriod);
        dateFrom = format(from, "yyyy-MM-dd");
        dateTo = format(to, "yyyy-MM-dd");
      }
    } else {
      dateFrom = format(startOfWeek(new Date(baseDate), { weekStartsOn: 1 }), "yyyy-MM-dd");
      dateTo = format(endOfWeek(new Date(baseDate), { weekStartsOn: 1 }), "yyyy-MM-dd");
    }

    return {
      date: baseDate,
      view: initialView,
      query: "",
      filters: user_id ? [{ name: "USER", value: user_id }] : [],
      date_from: dateFrom,
      date_to: dateTo,
    };
  });

  const [pendingDate, setPendingDate] = useState(controller.date);
  const [hoursData, setHoursData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [scaledWeekdays, setScaledWeekdays] = useState(null);
  const [statsExtraHours, setStatsExtraHours] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const currentUserRoles = useSelector((state) => state.session.currentUser?.roles || []);
  const canProcessHours = currentUserRoles?.some((r) => r.name === "admin");

  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);

  const handleHourUpdated = useCallback((updated) => {
    if (!updated?.id) return;
    setHoursData((prev) => {
      if (!Array.isArray(prev)) return prev;
      const idx = prev.findIndex((h) => h?.id === updated.id);
      if (idx === -1) return prev;
      const next = prev.slice();
      next[idx] = { ...next[idx], ...updated };
      return next;
    });
  }, []);

  const updateController = useCallback((newController) => {
    // Update the ref to store the current date
    if (newController.date) {
      currentDateRef.current = newController.date;
    }
    setController(newController);
    if (onStateChange) {
      onStateChange({
        view: newController.view,
        date: newController.date,
        date_from: newController.date_from,
        date_to: newController.date_to,
        period_type: periodType,
      });
    }
  }, [onStateChange, periodType]);

  // Emit week hours label for employee picker (uke-strip header)
  useEffect(() => {
    if (!isUkeStrip || !onWeekHoursLabel || !user_id) return;
    const anchor = parseISO((controller.date || new Date().toISOString()).substring(0, 10));
    const currWeekStart = format(startOfWeek(anchor, { weekStartsOn: 1 }), "yyyy-MM-dd");
    const currWeekEnd = format(endOfWeek(anchor, { weekStartsOn: 1 }), "yyyy-MM-dd");
    const worked = hoursInDateRange(statsExtraHours, currWeekStart, currWeekEnd);
    onWeekHoursLabel(`${worked.toFixed(1)}t`);
  }, [isUkeStrip, user_id, statsExtraHours, controller.date, onWeekHoursLabel]);

  // Sync pendingDate when controller.date changes
  useEffect(() => {
    setPendingDate(controller.date);
  }, [controller.date]);

  // Update filters when selections change
  useEffect(() => {
    const newFilters = [];

    if (user_id) {
      newFilters.push({ name: "USER", value: user_id });
    }
    if (selectedProject?.id) {
      newFilters.push({ name: "PROJECT", value: selectedProject.id });
    }
    if (selectedProjectActivity?.id) {
      newFilters.push({ name: "PROJECT_ACTIVITY", value: selectedProjectActivity.id });
    }

    // Check if filters actually changed using ref
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(newFilters);
    
    if (filtersChanged) {
      prevFiltersRef.current = newFilters;
      
      // Only update filters, preserve all other controller state including date
      setController(prevController => ({
        ...prevController,
        filters: newFilters,
      }));
    }
  }, [user_id, selectedProject?.id, selectedProjectActivity?.id]);

  // Fetch hours data directly (all pages for correct totals)
  const fetchHoursData = useCallback(async () => {
    if (!user_id) return;

    setIsLoading(true);
    try {
      const all = await fetchAllHoursPaginated(controller);
      setHoursData(all);
    } catch (error) {
      console.error("Error fetching hours:", error);
    } finally {
      setIsLoading(false);
    }
  }, [
    controller.date,
    controller.date_from,
    controller.date_to,
    controller.view,
    controller.filters,
    controller.query,
    user_id,
  ]);

  // Work schedule + extra hours for calendar month / adjacent weeks (statistics)
  useEffect(() => {
    if (!user_id) {
      setScaledWeekdays(null);
      setStatsExtraHours([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setStatsLoading(true);
      try {
        const ws = await fetchUserWorkSchedule(user_id);
        const sw =
          ws && typeof ws === "object" && ws.scaled_weekdays && typeof ws.scaled_weekdays === "object"
            ? ws.scaled_weekdays
            : null;
        if (!cancelled) setScaledWeekdays(sw);

        const anchor = parseISO((controller.date || new Date().toISOString()).substring(0, 10));
        const monthStart = startOfMonth(anchor);
        const monthEnd = endOfMonth(anchor);
        const currWeekEnd = endOfWeek(anchor, { weekStartsOn: 1 });
        const prevWeekAnchor = subWeeks(anchor, 1);
        const prevWeekStart = startOfWeek(prevWeekAnchor, { weekStartsOn: 1 });
        const rangeStart = dfMin([monthStart, prevWeekStart]);
        const rangeEnd = dfMax([monthEnd, currWeekEnd]);
        const date_from = format(rangeStart, "yyyy-MM-dd");
        const date_to = format(rangeEnd, "yyyy-MM-dd");
        const filters = [{ name: "USER", value: user_id }];
        const extra = await fetchAllHoursPaginated({
          date_from,
          date_to,
          filters,
          view: controller.view,
          query: controller.query || "",
          date: controller.date,
        });
        if (!cancelled) setStatsExtraHours(extra);
      } catch (e) {
        console.error("Error loading hour statistics:", e);
        if (!cancelled) {
          setScaledWeekdays(null);
          setStatsExtraHours([]);
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user_id, controller.date, controller.view, controller.query, pushHour, removeHourId]);

  // Fetch data when controller or user_id changes
  useEffect(() => {
    fetchHoursData();
  }, [
    controller.date,
    controller.date_from,
    controller.date_to,
    controller.view,
    controller.filters,
    user_id,
  ]);

  // Apply in-memory updates when a new/edited hour is pushed from parent
  useEffect(() => {
    if (!pushHour) return;

    // If hour already exists in current dataset, update it unconditionally (no date/filter guards)
    const existsInCurrentData =
      Array.isArray(hoursData) &&
      hoursData.some((h) => String(h?.id) === String(pushHour?.id));

    if (existsInCurrentData) {
      setHoursData((prev) => {
        if (!Array.isArray(prev)) return [pushHour];
        const idx = prev.findIndex((h) => String(h?.id) === String(pushHour?.id));
        if (idx >= 0) {
          const next = prev.slice();
          next[idx] = { ...next[idx], ...pushHour };
          return next;
        }
        return [...prev, pushHour];
      });
      return;
    }

    // Only add new entries if they match current user/date/filter guards
    const hourUserId = pushHour?.user?.id || pushHour?.user_id;
    if (user_id && hourUserId && String(user_id) !== String(hourUserId)) return;

    const hourDateStr = (pushHour?.date || "").substring(0, 10);
    if (!hourDateStr) return;
    const fromStr = (controller.date_from || "").substring(0, 10);
    const toStr = (controller.date_to || "").substring(0, 10);
    if (fromStr && hourDateStr < fromStr) return;
    if (toStr && hourDateStr > toStr) return;

    const filterHasProject = !!selectedProject?.id;
    const filterHasProjectActivity = !!selectedProjectActivity?.id;
    const matchesProject =
      !filterHasProject ||
      (pushHour?.project?.id || pushHour?.project_id) === selectedProject?.id;
    const hourPaId = pushHour?.project_activity?.id || pushHour?.project_activity_id;
    const matchesProjectActivity =
      !filterHasProjectActivity ||
      (hourPaId != null &&
        String(hourPaId) === String(selectedProjectActivity?.id));
    if (!matchesProject || !matchesProjectActivity) return;

    setHoursData((prev) => {
      if (!Array.isArray(prev)) return [pushHour];
      const idx = prev.findIndex((h) => String(h?.id) === String(pushHour?.id));
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = { ...next[idx], ...pushHour };
        return next;
      }
      return [...prev, pushHour];
    });
  }, [pushHour, hoursData, user_id, controller.date_from, controller.date_to, selectedProject?.id, selectedProjectActivity?.id]);

  // Apply in-memory removal when a deletion is pushed from parent
  useEffect(() => {
    if (!removeHourId) return;
    setHoursData((prev) => {
      if (!Array.isArray(prev)) return prev;
      return prev.filter((h) => String(h?.id) !== String(removeHourId));
    });
  }, [removeHourId]);

  // (moved helpers to top)

  const getDateRangeForView = (date, view) => {
    const currentDate = new Date(date);
    let dateFrom, dateTo;

    if (view === "day") {
      dateFrom = format(currentDate, "yyyy-MM-dd");
      dateTo = format(currentDate, "yyyy-MM-dd");
    } else if (view === "week") {
      dateFrom = format(
        startOfWeek(currentDate, { weekStartsOn: 1 }),
        "yyyy-MM-dd"
      );
      dateTo = format(
        endOfWeek(currentDate, { weekStartsOn: 1 }),
        "yyyy-MM-dd"
      );
    } else if (view === "period") {
      const range = getPeriodRangeForDate(currentDate, periodType);
      dateFrom = format(range.from, "yyyy-MM-dd");
      dateTo = format(range.to, "yyyy-MM-dd");
    }

    return { dateFrom, dateTo };
  };

  const navigateDate = (direction) => {
    const currentDate = new Date(controller.date);
    let newDate;

    switch (controller.view) {
      case "day":
        newDate =
          direction === "next"
            ? addDays(currentDate, 1)
            : subDays(currentDate, 1);
        break;
      case "week":
        newDate =
          direction === "next"
            ? addWeeks(currentDate, 1)
            : subWeeks(currentDate, 1);
        break;
      case "period": {
        if (periodType === "day") {
          newDate = direction === "next" ? addDays(currentDate, 1) : subDays(currentDate, 1);
        } else if (periodType === "week") {
          newDate = direction === "next" ? addWeeks(currentDate, 1) : subWeeks(currentDate, 1);
        } else if (periodType === "month") {
          newDate = direction === "next" ? addMonths(currentDate, 1) : subMonths(currentDate, 1);
        } else if (periodType === "quarter") {
          newDate = direction === "next" ? addQuarters(currentDate, 1) : subQuarters(currentDate, 1);
        } else if (periodType === "year") {
          newDate = direction === "next" ? addYears(currentDate, 1) : subYears(currentDate, 1);
        } else {
          newDate = currentDate;
        }

        const range = getPeriodRangeForDate(newDate, periodType);
        updateController({
          ...controller,
          view: "period",
          date: newDate.toISOString().substring(0, 10),
          date_from: format(range.from, "yyyy-MM-dd"),
          date_to: format(range.to, "yyyy-MM-dd"),
        });
        return;
      }
      default:
        newDate = addWeeks(currentDate, direction === "next" ? 1 : -1);
        break;
    }

    const { dateFrom, dateTo } = getDateRangeForView(
      newDate.toISOString().substring(0, 10),
      controller.view
    );

    updateController({
      ...controller,
      date: newDate.toISOString().substring(0, 10),
      date_from: dateFrom,
      date_to: dateTo,
    });
  };

  const setViewMode = (view) => {
    let dateFrom, dateTo;
    if (view === "period") {
      const range = getPeriodRangeForDate(new Date(controller.date), periodType);
      dateFrom = format(range.from, "yyyy-MM-dd");
      dateTo = format(range.to, "yyyy-MM-dd");
    } else {
      const res = getDateRangeForView(controller.date, view);
      dateFrom = res.dateFrom;
      dateTo = res.dateTo;
    }

    updateController({
      ...controller,
      view,
      date_from: dateFrom,
      date_to: dateTo,
    });
  };

  const getCurrentPeriodText = () => {
    const currentDate = new Date(controller.date);

    switch (controller.view) {
      case "day": {
        return format(currentDate, "EEEE, d. MMMM yyyy", { locale: currentDateFnsLocale() });
      }
      case "week": {
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        const weekEnd = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(weekStart, "d. MMM", { locale: currentDateFnsLocale() })} - ${format(
          weekEnd,
          "d. MMM yyyy",
          { locale: currentDateFnsLocale() }
        )}`;
      }
      case "period": {
        return `${format(new Date(controller.date_from), "d. MMM yyyy", { locale: currentDateFnsLocale() })} - ${format(
          new Date(controller.date_to),
          "d. MMM yyyy",
          { locale: currentDateFnsLocale() }
        )}`;
      }
      default: {
        return format(currentDate, "MMMM yyyy", { locale: currentDateFnsLocale() });
      }
    }
  };

  const getHoursForDay = (day, hoursData) => {
    if (!hoursData || !Array.isArray(hoursData)) return [];

    return hoursData
      .filter((hour) => {
        const hourDate = new Date(hour.date);
        return isSameDay(hourDate, day);
      })
      .sort((a, b) => {
        // Sort by time if available, otherwise by creation date
        if (a.time && b.time) {
          return (
            new Date(`2000-01-01T${a.time}`).getTime() -
            new Date(`2000-01-01T${b.time}`).getTime()
          );
        }
        return (
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );
      });
  };

  const renderWeekView = (hoursData) => {
    const start = startOfWeek(new Date(controller.date), { weekStartsOn: 1 });
    const end = endOfWeek(new Date(controller.date), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });

    return (
      <div className="space-y-6">
        {days.map((day) => {
          const dayHours = getHoursForDay(day, hoursData);
          const totalHours = dayHours.reduce(
            (sum, hour) => sum + (parseFloat(hour.time) || 0),
            0
          );

          return (
            <div
              key={day.toISOString()}
              className={`border border-gray-200 rounded-lg overflow-hidden ${
                isSameDay(day, today) ? "ring-2 ring-blue-300" : ""
              }`}
            >
              <div className="bg-gray-50 px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                    <div>
                      <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                        {format(day, "EEEE, d. MMMM yyyy", { locale: currentDateFnsLocale() })}
                      </h3>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3 sm:space-x-4">
                    <div className="text-right">
                      <div className="text-sm sm:text-base font-bold text-blue-600">
                        {totalHours.toFixed(1)}h
                      </div>
                      <div className="text-xs text-gray-500">
                        {dayHours.length} {t("entries")}
                      </div>
                    </div>
                    {onNewHourClick && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onNewHourClick(format(day, "yyyy-MM-dd"));
                        }}
                        className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                        title={t("Add hour for this day")}
                      >
                        <span className="text-lg font-semibold">+</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4">
                {dayHours.length === 0 ? (
                  <div className="text-center text-gray-500 py-8">
                    {t("No hours recorded for this day")}
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {dayHours.map((hour) => (
                      <div
                        key={hour.id}
                        className={`p-3 sm:p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${hour.approved ? "border-green-300 ring-1 ring-green-100" : hour.not_approved ? "border-red-300 ring-1 ring-red-100" : "border-gray-200"}`}
                        onClick={() => onHourClick && onHourClick(hour.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-2 mb-1.5">
                              <span className="text-sm font-bold text-blue-600">
                                {hour.time}h
                              </span>
                              <span className="text-xs text-gray-500">
                                {hour.created_at
                                  ? format(new Date(hour.created_at), "HH:mm", { locale: currentDateFnsLocale() })
                                  : ""}
                              </span>
                              <AgreedOvertimeBadge hour={hour} />
                              <TripletexSyncBadge hour={hour} />
                            </div>
                            <h4 className="text-xs sm:text-sm font-medium text-gray-900 break-words mb-1.5">
                              {hour.description}
                            </h4>

                            <div className="space-y-0.5">
                              {hour.project?.id && (
                                <div className="flex items-center space-x-1.5">
                                  <BuildingLibraryIcon className="h-3.5 w-3.5 text-gray-400" />
                                  <p className="text-xs text-gray-600">
                                    <ProjectTitle project={hour?.project} />
                                  </p>
                                </div>
                              )}
                              {hourHasProjectActivity(hour) && (
                                <div className="flex items-center space-x-1.5">
                                  <WrenchIcon className="h-3.5 w-3.5 text-gray-400" />
                                  <p className="text-xs text-gray-600">
                                    {projectActivityLine(hour.project_activity) || t("project_activity.missing")}
                                  </p>
                                </div>
                              )}
                              {hour.report && (
                                <div className="flex items-center space-x-1.5">
                                  <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
                                  <p className="text-xs text-gray-600">
                                    <ReportTitle report={hour?.report} />
                                  </p>
                                </div>
                              )}
                              {hour.work_order?.id && (
                                <div className="flex items-center space-x-1.5">
                                  <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
                                  <p className="text-xs text-gray-600">
                                    <WorkOrderTitle
                                      workOrder={hour?.work_order}
                                    />
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 ml-3 sm:ml-4">
                            <span className="text-xs text-gray-400">{t("Edit")}</span>
                            {canProcessHours && (
                              <button
                                className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedHour(hour);
                                  setProcessDialogOpen(true);
                                }}
                              >
                                {t("Process")}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderDayView = (hoursData) => {
    const dayHours = getHoursForDay(new Date(controller.date), hoursData);
    const totalHours = dayHours.reduce(
      (sum, hour) => sum + (parseFloat(hour.time) || 0),
      0
    );
    const currentDay = new Date(controller.date);

    return (
      <div className="space-y-4">
        <div className={`border border-gray-200 rounded-lg overflow-hidden ${
          isSameDay(currentDay, today) ? "ring-2 ring-blue-300" : ""
        }`}>
          <div className="bg-gray-50 px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <CalendarIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500" />
                <div>
                  <h3 className="text-sm sm:text-base font-semibold text-gray-900">
                    {format(currentDay, "EEEE, d. MMMM yyyy", { locale: currentDateFnsLocale() })}
                  </h3>
                </div>
              </div>
              <div className="flex items-center space-x-3 sm:space-x-4">
                <div className="text-right">
                  <div className="text-sm sm:text-base font-bold text-blue-600">
                    {totalHours.toFixed(1)}h
                  </div>
                  <div className="text-xs text-gray-500">
                    {dayHours.length} {t("entries")}
                  </div>
                </div>
                {onNewHourClick && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNewHourClick(format(currentDay, "yyyy-MM-dd"));
                    }}
                    className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    title={t("Add hour for this day")}
                  >
                    <span className="text-lg font-semibold">+</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        {dayHours.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {t("No hours recorded for this day")}
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {dayHours.map((hour) => (
              <div
                key={hour.id}
                className={`p-3 sm:p-4 border rounded-lg cursor-pointer hover:bg-gray-50 ${hour.approved ? "border-green-300 ring-1 ring-green-100" : hour.not_approved ? "border-red-300 ring-1 ring-red-100" : "border-gray-200"}`}
                onClick={() => onHourClick && onHourClick(hour.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className="text-sm font-bold text-blue-600">
                        {hour.time}h
                      </span>
                      <span className="text-xs text-gray-500">
                        {hour.created_at
                          ? format(new Date(hour.created_at), "HH:mm", { locale: currentDateFnsLocale() })
                          : ""}
                      </span>
                      <AgreedOvertimeBadge hour={hour} />
                      <TripletexSyncBadge hour={hour} />
                    </div>
                    <h3 className="text-xs sm:text-sm font-medium text-gray-900 break-words mb-1">
                      {hour.description}
                    </h3>
                    {hour.project?.id && (
                      <div className="flex items-center space-x-1.5">
                        <BuildingLibraryIcon className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-xs text-gray-600">
                          <ProjectTitle project={hour?.project} />
                        </p>
                      </div>
                    )}
                    {hourHasProjectActivity(hour) && (
                      <div className="flex items-center space-x-1.5">
                        <WrenchIcon className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-xs text-gray-600">
                          {projectActivityLine(hour.project_activity) || t("project_activity.missing")}
                        </p>
                      </div>
                    )}

                    {hour.report && (
                      <div className="flex items-center space-x-1.5">
                        <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-xs text-gray-600">
                          <ReportTitle report={hour?.report} />
                        </p>
                      </div>
                    )}

                    {hour.work_order?.id && (
                      <div className="flex items-center space-x-1.5">
                        <DocumentTextIcon className="h-3.5 w-3.5 text-gray-400" />
                        <p className="text-xs text-gray-600">
                          <WorkOrderTitle workOrder={hour?.work_order} />
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-xs text-gray-400">{t("Edit")}</span>
                    {canProcessHours && (
                      <button
                        className="text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedHour(hour);
                          setProcessDialogOpen(true);
                        }}
                      >
                        {t("Process")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderPeriodView = (hoursData) => {
    if (!controller.date_from || !controller.date_to) return null;
    const start = new Date(controller.date_from);
    const end = new Date(controller.date_to);
    const days = eachDayOfInterval({ start, end });

    const groupMap = new Map();
    (hoursData || []).forEach((hour) => {
      const projectName = hour.project?.name || hour.project?.title || t("No project");
      const paLine = projectActivityLine(hour.project_activity);
      const activityName = paLine || t("project_activity.missing");
      const paKey = hour.project_activity?.id ?? hour.project_activity_id ?? "none";
      const key = `${hour.project?.id || "none"}|${paKey}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          projectName,
          activityName,
          totals: {},
          totalRow: 0,
        });
      }
      const group = groupMap.get(key);
      const dKey = format(new Date(hour.date), "yyyy-MM-dd");
      const value = parseFloat(hour.time) || 0;
      group.totals[dKey] = (group.totals[dKey] || 0) + value;
      group.totalRow += value;
    });

    const groups = Array.from(groupMap.values()).sort((a, b) => {
      if (a.projectName === b.projectName) return a.activityName.localeCompare(b.activityName);
      return a.projectName.localeCompare(b.projectName);
    });

    const columnTotals = days.map((day) => {
      const dKey = format(day, "yyyy-MM-dd");
      let sum = 0;
      groups.forEach((g) => {
        sum += g.totals[dKey] || 0;
      });
      return sum;
    });

    const grandTotal = columnTotals.reduce((a, b) => a + b, 0);

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t("hours.period_table.project_and_line")}
              </th>
              {days.map((day) => (
                <th key={day.toISOString()} className="px-1 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex flex-col items-center gap-1">
                    <span>{format(day, "d")}</span>
                    {onNewHourClick && (
                      <button
                        type="button"
                        title={t("Add hour for this day")}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNewHourClick(format(day, "yyyy-MM-dd"));
                        }}
                        className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        +
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                {t("Sum")}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {groups.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-sm text-gray-500" colSpan={days.length + 2}>{t("No hours in this period")}</td>
              </tr>
            ) : (
              groups.map((group, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-2 text-sm text-gray-900 whitespace-nowrap">
                    <div className="font-medium">{group.projectName}</div>
                    <div className="text-gray-600 text-xs">{group.activityName}</div>
                  </td>
                  {days.map((day) => {
                    const dKey = format(day, "yyyy-MM-dd");
                    const val = group.totals[dKey] || 0;
                    return (
                      <td key={dKey} className="px-2 py-2 text-center text-sm text-gray-900">
                        {val ? `${val.toFixed(1)}h` : ""}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right text-sm font-semibold text-gray-900 whitespace-nowrap">
                    {group.totalRow.toFixed(1)}h
                  </td>
                </tr>
              ))
            )}
          </tbody>
          {groups.length > 0 && (
            <tfoot className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">
                  {t("Total")}
                </th>
                {columnTotals.map((sum, i) => (
                  <th key={i} className="px-2 py-3 text-center text-xs font-semibold text-gray-700">
                    {sum ? `${sum.toFixed(1)}h` : ""}
                  </th>
                ))}
                <th className="px-3 py-3 text-right text-xs font-semibold text-gray-700 whitespace-nowrap">
                  {grandTotal.toFixed(1)}h
                </th>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    );
  };

  const goToToday = useCallback(() => {
    if (controller.view === "period") {
      const range = getPeriodRangeForDate(today, periodType);
      updateController({
        ...controller,
        date: today.toISOString().substring(0, 10),
        date_from: format(range.from, "yyyy-MM-dd"),
        date_to: format(range.to, "yyyy-MM-dd"),
      });
    } else {
      const { dateFrom, dateTo } = getDateRangeForView(
        today.toISOString().substring(0, 10),
        controller.view
      );
      updateController({
        ...controller,
        date: today.toISOString().substring(0, 10),
        date_from: dateFrom,
        date_to: dateTo,
      });
    }
  }, [controller, periodType, updateController, getDateRangeForView]);

  const toggleStats = useCallback(() => setStatsDialogOpen((open) => !open), []);

  useImperativeHandle(ref, () => ({
    goToToday,
    setView: setViewMode,
    toggleStats,
    isStatsVisible: statsDialogOpen,
  }), [goToToday, setViewMode, toggleStats, statsDialogOpen]);

  const clearFilters = () => {
    setSelectedProject(null);
    setSelectedProjectActivity(null);
  };

  const activeFiltersCount = [
    selectedProject?.id ? 1 : 0,
    selectedProjectActivity?.id ? 1 : 0,
  ].reduce((sum, value) => sum + value, 0);

  const currentPeriodLabel = getCurrentPeriodText();

  const renderHoursStatistics = () => {
    if (!user_id) return null;
    const fromStr = (controller.date_from || "").substring(0, 10);
    const toStr = (controller.date_to || "").substring(0, 10);
    const workedPeriod = sumHoursList(hoursData);
    const normalPeriod =
      fromStr && toStr ? normalHoursBetween(fromStr, toStr, scaledWeekdays) : null;
    const pctPeriod =
      normalPeriod != null && normalPeriod > 0
        ? Math.min(999, Math.round((workedPeriod / normalPeriod) * 1000) / 10)
        : null;

    const anchorStr = (controller.date || new Date().toISOString()).substring(0, 10);
    const anchor = parseISO(anchorStr);
    const mFrom = format(startOfMonth(anchor), "yyyy-MM-dd");
    const mTo = format(endOfMonth(anchor), "yyyy-MM-dd");
    const workedMonth = hoursInDateRange(statsExtraHours, mFrom, mTo);
    const normalMonth = normalHoursBetween(mFrom, mTo, scaledWeekdays);
    const pctMonth =
      normalMonth != null && normalMonth > 0
        ? Math.min(999, Math.round((workedMonth / normalMonth) * 1000) / 10)
        : null;

    const currWeekStart = format(startOfWeek(anchor, { weekStartsOn: 1 }), "yyyy-MM-dd");
    const currWeekEnd = format(endOfWeek(anchor, { weekStartsOn: 1 }), "yyyy-MM-dd");
    const prevA = subWeeks(anchor, 1);
    const prevWeekStart = format(startOfWeek(prevA, { weekStartsOn: 1 }), "yyyy-MM-dd");
    const prevWeekEnd = format(endOfWeek(prevA, { weekStartsOn: 1 }), "yyyy-MM-dd");
    const workedThisWeek = hoursInDateRange(statsExtraHours, currWeekStart, currWeekEnd);
    const workedPrevWeek = hoursInDateRange(statsExtraHours, prevWeekStart, prevWeekEnd);
    const normalThisWeek = normalHoursBetween(currWeekStart, currWeekEnd, scaledWeekdays);
    const normalPrevWeek = normalHoursBetween(prevWeekStart, prevWeekEnd, scaledWeekdays);
    const pctThisWeek =
      normalThisWeek != null && normalThisWeek > 0
        ? Math.min(999, Math.round((workedThisWeek / normalThisWeek) * 1000) / 10)
        : null;
    const pctPrevWeek =
      normalPrevWeek != null && normalPrevWeek > 0
        ? Math.min(999, Math.round((workedPrevWeek / normalPrevWeek) * 1000) / 10)
        : null;

    const todayStr = format(today, "yyyy-MM-dd");
    const workedToday = hoursInDateRange(statsExtraHours, todayStr, todayStr);

    const fmt = (x) => (typeof x === "number" && !isNaN(x) ? `${x.toFixed(1)}h` : "—");
    const fmtN = (x) => (x != null && typeof x === "number" && !isNaN(x) ? `${x.toFixed(1)}h` : "—");
    const fmtPct = (x) => (x != null && !isNaN(x) ? `${x}%` : "—");

    const statBlock = (label, worked, normal, pct, icon = CalendarIcon) => {
      const Icon = icon;

      return (
        <div className="rounded-2xl border border-slate-200 bg-white px-3 py-3 sm:px-4 sm:py-4 shadow-sm">
          <div className="flex items-start justify-between gap-2 sm:gap-3">
            <div className="min-w-0">
              <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {label}
              </div>
              <div className="mt-2 sm:mt-3 text-xl sm:text-2xl font-semibold tracking-tight text-slate-950">
                {fmt(worked)}
              </div>
            </div>
            <div className="flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
              <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
            </div>
          </div>

          {scaledWeekdays ? (
            <div className="mt-2 sm:mt-3 flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs text-slate-600">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 sm:px-2.5 sm:py-1">
                {fmtN(normal)} {t("hours.sheet.stats_normal_suffix")}
              </span>
              {pct != null ? (
                <span className="rounded-full bg-blue-50 px-2 py-0.5 sm:px-2.5 sm:py-1 font-medium text-blue-700">
                  {fmtPct(pct)}
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      );
    };

    return (
      <>
        {/* Compact mobile stats bar — always visible on mobile, hidden on sm+ */}
        <div className="sm:hidden mb-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
          <span className="text-slate-500 font-medium">{t("Week")}:</span>
          <span className="font-semibold text-slate-900">{fmt(workedThisWeek)}</span>
          <span className="text-slate-300 select-none">·</span>
          <span className="text-slate-500 font-medium">{t("hours.sheet.stats_compact_month")}:</span>
          <span className="font-semibold text-slate-900">{fmt(workedMonth)}</span>
          <button
            type="button"
            onClick={() => setStatsDialogOpen(true)}
            className="ml-auto flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold text-slate-600 hover:bg-slate-100"
          >
            {t("hours.sheet.stats_show_more")}
            <ChevronRightIcon className="h-3 w-3" />
          </button>
        </div>

        {/* Full stats section — desktop/tablet only */}
        <section className="mb-4 sm:mb-6 rounded-[2rem] border border-slate-200 bg-slate-50/80 p-3 sm:p-5 hidden sm:block">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t("hours.sheet.stats_title")}
              </div>
              <div className="mt-1 text-xs sm:text-sm text-slate-600">{currentPeriodLabel}</div>
            </div>
            {!scaledWeekdays && !statsLoading ? (
              <div className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700">
                {t("hours.sheet.stats_no_schedule")}
              </div>
            ) : null}
            {statsLoading ? (
              <div className="rounded-full bg-white px-3 py-1.5 text-xs text-slate-500">
                {t("hours.sheet.stats_loading_extra")}
              </div>
            ) : null}
          </div>

          <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
            {statBlock(
              t("hours.sheet.stats_selected_period"),
              workedPeriod,
              normalPeriod,
              pctPeriod,
              CalendarIcon
            )}
            {statBlock(
              t("hours.sheet.stats_calendar_month"),
              workedMonth,
              normalMonth,
              pctMonth,
              ArrowTrendingUpIcon
            )}
            <div className="col-span-2 sm:col-span-1 rounded-2xl border border-slate-200 bg-white px-3 py-3 sm:px-4 sm:py-4 shadow-sm">
              <div className="flex items-start justify-between gap-2 sm:gap-3">
                <div>
                  <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("hours.sheet.stats_weeks_heading")}
                  </div>
                  <div className="mt-1 text-xs sm:text-sm text-slate-600">{t("hours.sheet.stats_this_week")}</div>
                </div>
                <div className="flex h-8 w-8 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-600">
                  <AdjustmentsHorizontalIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
              </div>

              <div className="mt-3 sm:mt-4 grid grid-cols-2 gap-2 sm:gap-3">
                <div className="rounded-2xl bg-slate-50 px-2 py-2 sm:px-3 sm:py-3">
                  <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("hours.sheet.stats_this_week")}
                  </div>
                  <div className="mt-1.5 sm:mt-2 text-base sm:text-xl font-semibold text-slate-950">{fmt(workedThisWeek)}</div>
                  {scaledWeekdays ? (
                    <div className="mt-1 sm:mt-2 text-xs text-slate-600">
                      {fmtN(normalThisWeek)} {t("hours.sheet.stats_normal_suffix")}
                      {pctThisWeek != null ? ` • ${fmtPct(pctThisWeek)}` : ""}
                    </div>
                  ) : null}
                </div>
                <div className="rounded-2xl bg-slate-50 px-2 py-2 sm:px-3 sm:py-3">
                  <div className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {t("hours.sheet.stats_prev_week")}
                  </div>
                  <div className="mt-1.5 sm:mt-2 text-base sm:text-xl font-semibold text-slate-950">{fmt(workedPrevWeek)}</div>
                  {scaledWeekdays ? (
                    <div className="mt-1 sm:mt-2 text-xs text-slate-600">
                      {fmtN(normalPrevWeek)} {t("hours.sheet.stats_normal_suffix")}
                      {pctPrevWeek != null ? ` • ${fmtPct(pctPrevWeek)}` : ""}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile Oversikt dialog — full-screen stats overview */}
        <PMDialog
          open={statsDialogOpen}
          onClose={() => setStatsDialogOpen(false)}
          title={t("hours.sheet.stats_overview_title")}
          dense={true}
        >
          <div className="flex flex-col gap-4 overflow-y-auto p-4">
            <div className="flex flex-col gap-1">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                {t("hours.sheet.stats_title")}
              </div>
              <div className="text-xs text-slate-600">{currentPeriodLabel}</div>
              {!scaledWeekdays && !statsLoading && (
                <div className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 self-start">
                  {t("hours.sheet.stats_no_schedule")}
                </div>
              )}
              {statsLoading && (
                <div className="text-xs text-slate-500">{t("hours.sheet.stats_loading_extra")}</div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              {statBlock(t("hours.sheet.stats_selected_period"), workedPeriod, normalPeriod, pctPeriod, CalendarIcon)}
              {statBlock(t("hours.sheet.stats_calendar_month"), workedMonth, normalMonth, pctMonth, ArrowTrendingUpIcon)}
              <div className="rounded-2xl bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("hours.sheet.stats_this_week")}
                </div>
                <div className="mt-1.5 text-lg font-semibold text-slate-950">{fmt(workedThisWeek)}</div>
                {scaledWeekdays && (
                  <div className="mt-1 text-xs text-slate-600">
                    {fmtN(normalThisWeek)} {t("hours.sheet.stats_normal_suffix")}
                    {pctThisWeek != null ? ` • ${fmtPct(pctThisWeek)}` : ""}
                  </div>
                )}
              </div>
              <div className="rounded-2xl bg-slate-50 px-3 py-3">
                <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("hours.sheet.stats_prev_week")}
                </div>
                <div className="mt-1.5 text-lg font-semibold text-slate-950">{fmt(workedPrevWeek)}</div>
                {scaledWeekdays && (
                  <div className="mt-1 text-xs text-slate-600">
                    {fmtN(normalPrevWeek)} {t("hours.sheet.stats_normal_suffix")}
                    {pctPrevWeek != null ? ` • ${fmtPct(pctPrevWeek)}` : ""}
                  </div>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={() => setStatsDialogOpen(false)}
              className="mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 active:bg-slate-100"
            >
              {t("hours.sheet.stats_close")}
            </button>
          </div>
        </PMDialog>
      </>
    );
  };

  if (isUkeStrip) {
    return (
      <>
      <HourBillingSelection hours={hoursData} disabled={isLoading} onSaved={fetchHoursData} />
      <HoursUkeStripBody
        controller={controller}
        hoursData={hoursData}
        statsExtraHours={statsExtraHours}
        scaledWeekdays={scaledWeekdays}
        isLoading={isLoading}
        statsLoading={statsLoading}
        today={today}
        periodType={periodType}
        setPeriodType={setPeriodType}
        showFilters={showFilters}
        showFiltersMenu={showFiltersMenu}
        setShowFiltersMenu={setShowFiltersMenu}
        selectedProject={selectedProject}
        setSelectedProject={setSelectedProject}
        selectedProjectActivity={selectedProjectActivity}
        setSelectedProjectActivity={setSelectedProjectActivity}
        activeFiltersCount={activeFiltersCount}
        clearFilters={clearFilters}
        navigateDate={navigateDate}
        goToToday={goToToday}
        updateController={updateController}
        getDateRangeForView={getDateRangeForView}
        onHourClick={onHourClick}
        onNewHourClick={onNewHourClick}
        canProcessHours={canProcessHours}
        selectedHour={selectedHour}
        setSelectedHour={setSelectedHour}
        processDialogOpen={processDialogOpen}
        setProcessDialogOpen={setProcessDialogOpen}
        handleHourUpdated={handleHourUpdated}
        infoBannerHidden={infoBannerHidden}
        setInfoBannerHidden={setInfoBannerHidden}
        statsDialogOpen={statsDialogOpen}
        setStatsDialogOpen={setStatsDialogOpen}
      />
      </>
    );
  }

  return (
    <div className={`rounded-[2rem] border border-slate-200 bg-white shadow-sm ${className}`}>
      <HourBillingSelection hours={hoursData} disabled={isLoading} onSaved={fetchHoursData} />
      <div className="border-b border-slate-200 p-4 sm:p-6">
        <div className="rounded-[2rem] border border-slate-200 bg-gradient-to-br from-white via-slate-50/70 to-slate-100/80 p-4 sm:p-5">
          <div className="flex flex-col gap-5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-600">
                  <CalendarIcon className="h-4 w-4" />
                  {t("Hours")}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Mobile-only filter button — top-right corner, icon-only */}
                {showFilters ? (
                  <button
                    onClick={() => setShowFiltersMenu(!showFiltersMenu)}
                    className={`relative sm:hidden inline-flex h-8 w-8 items-center justify-center rounded-xl border transition ${
                      showFiltersMenu || activeFiltersCount > 0
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    title={t("Filters")}
                  >
                    <FunnelIcon className="h-4 w-4" />
                    {activeFiltersCount > 0 && (
                      <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[9px] font-bold text-white">
                        {activeFiltersCount}
                      </span>
                    )}
                  </button>
                ) : null}

                {/* View toggle — hidden on mobile (moved to mobile bottom nav) */}
                <div className="hidden sm:inline-flex w-full rounded-2xl bg-white/90 p-1 shadow-sm ring-1 ring-slate-200 lg:w-auto">
                  {["week", "day", "period"].map((view) => (
                    <button
                      key={view}
                      onClick={() => setViewMode(view)}
                      className={`flex-1 rounded-2xl px-2 py-1.5 text-xs sm:px-4 sm:py-2 sm:text-sm font-medium transition-colors lg:flex-none ${
                        controller.view === view
                          ? "bg-slate-950 text-white shadow-sm"
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                      }`}
                    >
                      {view === "week" ? t("Week") : view === "day" ? t("Day") : t("Period")}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_auto]">
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-3 sm:p-4">
                {controller.view === "period" ? (
                  <PeriodSelector
                    periodType={periodType}
                    baseDate={controller.date}
                    from={controller.date_from}
                    to={controller.date_to}
                    onChange={({ periodType: nextType, from, to }) => {
                      setPeriodType(nextType || periodType);
                      updateController({
                        ...controller,
                        view: "period",
                        date_from: from,
                        date_to: to,
                      });
                    }}
                  />
                ) : (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-2 sm:justify-start">
                      <button
                        onClick={() => navigateDate("prev")}
                        className="inline-flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                      >
                        <ChevronLeftIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      <div className="min-w-0 flex-1 px-2 text-center">
                        {/* Clicking the date label opens the hidden PMDatePicker (calendar.js pattern) */}
                        <button
                          type="button"
                          onClick={() => datePickerInputRef.current?.click()}
                          className="w-full truncate text-sm sm:text-base font-semibold text-slate-950 hover:text-blue-600 focus:outline-none focus:underline"
                        >
                          {currentPeriodLabel}
                        </button>
                      </div>
                      <button
                        onClick={() => navigateDate("next")}
                        className="inline-flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 text-slate-600 transition hover:border-slate-300 hover:bg-slate-100 hover:text-slate-900"
                      >
                        <ChevronRightIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      {/* Today button — mobile only, adjacent to right nav arrow */}
                      <button
                        onClick={goToToday}
                        className="sm:hidden inline-flex h-9 items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                      >
                        {t("Today")}
                      </button>
                    </div>

                    {/* Hidden PMDatePicker – navigates immediately when date is picked (mobile-friendly, mirrors calendar.js) */}
                    <div className="sr-only">
                      <PMDatePicker
                        inputRef={datePickerInputRef}
                        name="hours_view_date_nav"
                        value={controller.date}
                        onChange={(e) => {
                          const val = e?.target?.value;
                          if (!val) return;
                          const { dateFrom, dateTo } = getDateRangeForView(val, controller.view);
                          updateController({ ...controller, date: val, date_from: dateFrom, date_to: dateTo });
                        }}
                      />
                    </div>

                    {/* Desktop: explicit date picker + Go button (hidden on mobile) */}
                    <div className="hidden sm:flex flex-col gap-2 sm:flex-row">
                      <div className="flex-1 [&_input]:h-11 [&_input]:rounded-2xl [&_input]:border-slate-200 [&_input]:bg-slate-50 [&_input]:pl-10 [&_input]:pr-3 [&_input]:text-sm [&_input]:text-slate-700 [&_input]:shadow-none [&_input]:focus:border-blue-500 [&_input]:focus:ring-2 [&_input]:focus:ring-blue-500">
                        <PMDatePicker
                          name="hours_view_date"
                          value={pendingDate}
                          placeholder={t("Select date")}
                          onChange={(event) => setPendingDate(event?.target?.value || "")}
                        />
                      </div>
                      <button
                        onClick={() => {
                          const { dateFrom, dateTo } = getDateRangeForView(
                            pendingDate,
                            controller.view
                          );
                          updateController({
                            ...controller,
                            date: pendingDate,
                            date_from: dateFrom,
                            date_to: dateTo,
                          });
                        }}
                        className="inline-flex h-11 items-center justify-center rounded-2xl bg-slate-950 px-5 text-sm font-semibold text-white transition hover:bg-slate-800"
                      >
                        {t("Go")}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-2 sm:flex-row xl:flex-col">
                {showFilters ? (
                  <button
                    onClick={() => setShowFiltersMenu(!showFiltersMenu)}
                    className={`hidden sm:inline-flex h-9 sm:h-11 items-center justify-center gap-1.5 sm:gap-2 rounded-2xl border px-2.5 sm:px-4 text-xs sm:text-sm font-semibold transition ${
                      showFiltersMenu || activeFiltersCount > 0
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    title={t("Filters")}
                  >
                    <FunnelIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    <span>{t("Filters")}</span>
                    {activeFiltersCount > 0 ? (
                      <span className="rounded-full bg-white/15 px-1.5 py-0.5 text-xs text-white">
                        {activeFiltersCount}
                      </span>
                    ) : null}
                  </button>
                ) : null}

                {/* Today button — desktop only; mobile version is inline with nav arrows */}
                <button
                  onClick={goToToday}
                  className="hidden sm:inline-flex h-11 items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
                >
                  {t("Today")}
                </button>
              </div>
            </div>

            {showFiltersMenu ? (
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                        {t("Filters")}
                      </div>
                      <div className="mt-1 text-sm text-slate-600">
                        {t("Narrow down hours by project or activity")}
                      </div>
                    </div>
                    {activeFiltersCount > 0 ? (
                      <button
                        onClick={clearFilters}
                        className="text-sm font-medium text-slate-600 underline-offset-4 hover:text-slate-900 hover:underline"
                      >
                        {t("Clear Filters")}
                      </button>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        {t("Project Filter")}
                      </label>
                      <ProjectSelector
                        noLabel
                        name="project_filter"
                        selected={selectedProject}
                        onChange={(data) => {
                          const obj = data?.target?.object;
                          setSelectedProject(obj);
                          if (!obj?.id) {
                            setSelectedProjectActivity(null);
                          }
                        }}
                        placeholder={t("All Projects")}
                      />
                    </div>
                    <div>
                      <label className="mb-2 block text-sm font-medium text-slate-700">
                        {t("project_activity.label")}
                      </label>
                      <ModernProjectActivitySelectorField
                        name="project_activity_filter_id"
                        label={false}
                        selected={selectedProjectActivity}
                        project={selectedProject?.id ? selectedProject : null}
                        setFieldValue={(field, val) => {
                          if (field === "project_activity") {
                            setSelectedProjectActivity(val);
                          }
                          if (
                            field === "project_activity_filter_id" &&
                            (val == null || val === "")
                          ) {
                            setSelectedProjectActivity(null);
                          }
                        }}
                        errors={{}}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Hours Content */}
      <div className="p-4 sm:p-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-sm text-gray-500">
              {t("Loading hours...")}
            </p>
          </div>
        ) : (
          <>
            {renderHoursStatistics()}
            {controller.view === "week" && renderWeekView(hoursData)}
            {controller.view === "day" && renderDayView(hoursData)}
            {controller.view === "period" && renderPeriodView(hoursData)}
            {canProcessHours && (
              <ApproveHourDialog
                hour={selectedHour}
                open={processDialogOpen}
                onClose={() => {
                  setProcessDialogOpen(false);
                  setSelectedHour(null);
                }}
                onRefresh={handleHourUpdated}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
});

// Wrap with React.memo to prevent unnecessary re-renders
export const HoursViewComponent = memo(HoursViewComponentInternal);
